import type { FieldOptions, ParsedToken, RelationalFieldOption } from '../../../types/QueryBuilder';

// Innermost EXPRESSION.expressionType whose location contains `offset`, or undefined at top level.
export function expressionTypeAtOffset(tokens: ParsedToken[], offset: number): string | undefined {
  let result: string | undefined;
  const visit = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type !== 'EXPRESSION') {
        continue;
      }
      if (offset > token.location.start.offset && offset < token.location.end.offset) {
        result = token.expressionType;
        visit(token.inner);
      }
    }
  };
  visit(tokens);
  return result;
}

export function expressionTypeEndingAt(tokens: ParsedToken[], offset: number): string | undefined {
  let latest: ParsedToken | undefined;
  const visit = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.location.end.offset <= offset && (!latest || token.location.end.offset > latest.location.end.offset)) {
        latest = token;
      }
      if (token.type === 'EXPRESSION') {
        visit(token.inner);
      }
    }
  };
  visit(tokens);
  return latest?.type === 'EXPRESSION' ? latest.expressionType : undefined;
}

export function canInsertThenAt(
  parsedQuery: ParsedToken[],
  insertOffset: number,
  relationalFieldOptions: RelationalFieldOption[] | undefined,
): boolean {
  const expressionType = expressionTypeEndingAt(parsedQuery, insertOffset);
  if (!expressionType) {
    return false;
  }
  return relationalFieldOptions?.some((option) => option.value === expressionType && option.sequenceable) ?? false;
}

// The scope a group of the given expression type confers on the fields inside it.
export function scopeForExpressionType(
  expressionType: string | undefined,
  relationalFieldOptions: RelationalFieldOption[] | undefined,
): string | undefined {
  if (!expressionType) {
    return undefined;
  }
  return relationalFieldOptions?.find((option) => option.value === expressionType)?.scope;
}

// Field options valid in the given scope. Untagged options (scope undefined) are the base scope.
export function fieldOptionsForScope(options: FieldOptions[], scope: string | undefined): FieldOptions[] {
  return options.filter((option) => (option.scope ?? undefined) === (scope ?? undefined));
}
